export type SeriesPattern = "DAILY" | "WEEKDAYS" | "WEEKLY"

export type NormalizedTimeInput = {
  hours: number
  minutes: number
  normalized: string
}

export const padTimePart = (value: number) => String(value).padStart(2, "0")

export const weekdayIndex = (date: Date) => (date.getDay() + 6) % 7

export const normalizeTimeInput = (raw: string): NormalizedTimeInput | null => {
  const trimmed = (raw ?? "").trim()

  if (!trimmed) return null

  const match = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)

  if (!match) return null

  const hour = Number(match[1])
  const minute = Number(match[2])

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

  return { hours: hour, minutes: minute, normalized: `${padTimePart(hour)}:${padTimePart(minute)}` }
}

export const resolveSeriesStartTime = ({
  rawTime,
  fallbackStart,
}: {
  rawTime: string
  fallbackStart?: Date | null
}): NormalizedTimeInput | null => {
  const parsed = normalizeTimeInput(rawTime)

  if (parsed) return parsed

  if (!fallbackStart) return null

  return {
    hours: fallbackStart.getHours(),
    minutes: fallbackStart.getMinutes(),
    normalized: `${padTimePart(fallbackStart.getHours())}:${padTimePart(fallbackStart.getMinutes())}`,
  }
}

export const buildSeriesOccurrenceDays = ({
  rangeStart,
  rangeEnd,
  pattern,
  weekdays,
  maxOccurrences,
}: {
  rangeStart?: Date | null
  rangeEnd?: Date | null
  pattern: SeriesPattern
  weekdays: number[]
  maxOccurrences: number
}) => {
  if (!rangeStart || !rangeEnd)
    return { ok: false as const, reason: "Bitte Zeitraum (Von/Bis) wählen." }

  const start = new Date(rangeStart)
  start.setHours(0, 0, 0, 0)
  const end = new Date(rangeEnd)
  end.setHours(0, 0, 0, 0)

  if (end.getTime() < start.getTime()) {
    return { ok: false as const, reason: "Zeitraum ist ungültig (Bis liegt vor Von)." }
  }

  const selectedWeekdays = pattern === "WEEKLY" ? (Array.isArray(weekdays) ? weekdays : []) : null

  if (pattern === "WEEKLY" && !selectedWeekdays?.length) {
    return { ok: false as const, reason: "Bitte mindestens einen Wochentag auswählen." }
  }

  const days: Date[] = []
  const cursor = new Date(start)
  while (cursor.getTime() <= end.getTime()) {
    const wd = weekdayIndex(cursor)
    const include =
      pattern === "DAILY"
        ? true
        : pattern === "WEEKDAYS"
          ? wd < 5
          : selectedWeekdays
            ? selectedWeekdays.includes(wd)
            : false

    if (include) {
      days.push(new Date(cursor))

      if (days.length > maxOccurrences) {
        return {
          ok: false as const,
          reason: `Zu viele Einträge (${days.length}+). Bitte Zeitraum eingrenzen (max. ${maxOccurrences}).`,
        }
      }
    }

    cursor.setDate(cursor.getDate() + 1)
  }

  if (!days.length) {
    return {
      ok: false as const,
      reason: "Serie würde keine Einträge erzeugen. Bitte Regel/Zeitraum prüfen.",
    }
  }

  return { ok: true as const, days }
}

export const computeDurationMinutes = (startIso?: string | null, endIso?: string | null) => {
  if (!startIso || !endIso) return null

  const start = new Date(startIso)
  const end = new Date(endIso)
  const diff = (end.getTime() - start.getTime()) / 60000

  if (!Number.isFinite(diff) || diff <= 0) return null

  return Math.round(diff)
}
